import React from 'react';

import { connect } from 'react-redux';

import { orgSelector } from '../../reducers/org-selectors';

import { UiButton } from '../../ui/ui-button';

import { JobFlowButtons } from './job-flow-buttons';

import { lowercaseFirstLetter } from './util/lowercase-first-letter';
import { combineWords } from '../../util/formatters';

const mapStateToProps = (state: any, ownProps: InputProps) => ({
  org: orgSelector(state, ownProps.orgId),
});

interface InputProps extends JobFlowStep {
  orgId: number;
  job: Partial<Job>;
}

type State = {
  step: number;
  jobResponsibilities: string;
  employeeAttrs: Array<string>;
  employerAttrs: Array<string>;
  tagline: string;
  shortDescription: string;
};

type StateProps = ReturnType<typeof mapStateToProps>;
type Props = InputProps & StateProps;

const EMPLOYEE_ATTRIBUTES = [
  'Dedicated',
  'Friendly',
  'Motivated',
  'Career-minded',
  'Efficient',
  'Experienced',
  'Independent',
  'Reliable',
  'Smart',
  'Courteous',
  'Dependable',
];

const EMPLOYER_ATTRIBUTES = [
  'Great Benefits',
  'Flexible Hours',
  'On-the-job Training',
  'Family Owned',
  'A Friendly Team',
  'Competitive Wages',
  'Exciting Work',
];

type WordSelectorProps = {
  words: Array<string>;
  selectedWords: Array<string>;
  onChange: Function;
  limit?: number;
};

class WordSelector extends React.PureComponent<WordSelectorProps, State> {
  static defaultProps = {
    limit: 0,
  };

  atLimit = () => {
    const { limit, selectedWords } = this.props;
    return limit && selectedWords.length === limit;
  };

  handleChange = (word: string) => () => {
    const { selectedWords, onChange } = this.props;
    if (selectedWords.includes(word)) {
      onChange(selectedWords.filter((w) => w !== word));
    } else if (!this.atLimit()) {
      onChange(selectedWords.concat([word]));
    }
  };

  render() {
    const { selectedWords, words } = this.props;
    return (
      <div className="u-display-flex u-flexWrap-wrap u-justifyContent-center">
        {words.map((word) => (
          <UiButton
            className="u-flexShrink-0 u-margin-left-sm u-margin-right-sm u-margin-bottom"
            selected={selectedWords.includes(word)}
            variant="tertiary"
            disabled={Boolean(!selectedWords.includes(word) && this.atLimit())}
            key={word}
            onClick={this.handleChange(word)}
          >
            {word}
          </UiButton>
        ))}
      </div>
    );
  }
}

class JobFlowShortDescriptionComponent extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      step: props.job.tagline || props.job.shortDescription ? 3 : 0,
      jobResponsibilities: '',
      employeeAttrs: [],
      employerAttrs: [],
      shortDescription: props.job.shortDescription || '',
      tagline: props.job.tagline || '',
    };
  }

  handleWordSelect =
    (stateField: 'employeeAttrs' | 'employerAttrs') => (selectedWords: Array<string>) => {
      this.setState({ [stateField]: selectedWords } as any);
    };

  handleTextChange =
    (key: 'jobResponsibilities' | 'shortDescription' | 'tagline') =>
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      this.props.onDirty(true);
      this.setState({ [key]: e.target.value } as any);
    };

  handlePrev = () => {
    const { onPrev, job, editing } = this.props;
    if (!editing && (this.state.step === 0 || job.tagline || job.shortDescription)) {
      onPrev();
    } else {
      this.setState((prevState) => ({ step: prevState.step - 1 }));
    }
  };

  handleNext = () => {
    if (this.state.step === 3) {
      this.props.onNext({
        shortDescription: this.state.shortDescription,
        tagline: this.state.tagline,
      });
    } else {
      this.setState((prevState) => ({
        shortDescription: this.buildShortDescription(),
        tagline: this.buildTagline(),
        step: prevState.step + 1,
      }));
    }
  };

  handleReset = () => {
    this.setState({ step: 0 });
  };

  buildShortDescription = () => {
    const { jobResponsibilities } = this.state;
    const { job, org, onDirty } = this.props;
    if (!org) {
      return '';
    }
    const employeeAttrs = combineWords(this.state.employeeAttrs.map((attr) => attr.toLowerCase()));
    const employerAttrs = combineWords(this.state.employerAttrs.map((attr) => attr.toLowerCase()));
    const shouldAddPeriod = !['!', '.'].includes(jobResponsibilities.slice(-1));

    onDirty(true);
    return [
      `${org.name} has ${job.title} positions available for ${employeeAttrs} team members.`,
      `You'll ${lowercaseFirstLetter(jobResponsibilities)}${shouldAddPeriod ? '.' : ''}`,
      `Join for ${employerAttrs}.`,
    ].join(' ');
  };

  buildTagline = () => {
    const employerAttrs = combineWords(this.state.employerAttrs.map((attr) => attr.toLowerCase()));
    return `${employerAttrs.charAt(0).toUpperCase()}${employerAttrs.slice(1)}!`;
  };

  render() {
    let buttonDisabled;
    const { step, jobResponsibilities, employeeAttrs, employerAttrs, shortDescription, tagline } =
      this.state;
    const { onSkip, job, org, saving, editing } = this.props;
    const { handleNext, handlePrev } = this;

    switch (step) {
      case 0:
        buttonDisabled = this.state.employeeAttrs.length === 0;
        break;
      case 1:
        buttonDisabled = !this.state.jobResponsibilities;
        break;
      case 2:
        buttonDisabled = this.state.employerAttrs.length === 0;
        break;
      case 3:
        buttonDisabled = !this.state.tagline || !this.state.shortDescription;
        break;
      default:
        return false;
    }

    return (
      <div className="u-margin-top-xl">
        {step === 0 && (
          <>
            <div className="typo-h5 u-textAlign-center">
              What are the top qualities you&apos;re looking for in a {job.title}?
            </div>
            <div className="typo-subtitle u-textAlign-center u-margin-bottom-2x">
              Select up to three qualities
            </div>
            <WordSelector
              words={EMPLOYEE_ATTRIBUTES}
              onChange={this.handleWordSelect('employeeAttrs')}
              selectedWords={employeeAttrs}
              limit={3}
            />
          </>
        )}
        {step === 1 && (
          <div className="u-textAlign-center">
            <div className="typo-h5 u-margin-bottom-2x">
              What&apos;s one sentence that summarizes the responsibilities of a {job.title}?
            </div>
            <div className="u-maxWidth-550px u-display-inlineBlock">
              <div className="typo-h6 u-textAlign-left">A {job.title} will...</div>
              <textarea
                cols={200}
                rows={3}
                placeholder="Greet customers, work the checkout counter registers, and help stock the shelves with product"
                className="typo-title u-width-100 u-padding"
                onChange={this.handleTextChange('jobResponsibilities')}
                value={jobResponsibilities}
              />
            </div>
          </div>
        )}
        {step === 2 && (
          <>
            <div className="typo-h5 u-textAlign-center">
              What are the best parts of working {org ? `at ${org.name}` : 'here'}?
            </div>
            <div className="typo-subtitle u-textAlign-center u-margin-bottom-2x">
              Select up to three benefits
            </div>
            <WordSelector
              words={EMPLOYER_ATTRIBUTES}
              onChange={this.handleWordSelect('employerAttrs')}
              selectedWords={employerAttrs}
              limit={3}
            />
          </>
        )}
        {step === 3 && (
          <div className="u-textAlign-center">
            {!editing && (
              <div className="typo-h5 u-margin-bottom-2x">
                Great! Based on your answers, here&apos;s a summary description and an advertising
                tagline we&apos;ve built for you.
              </div>
            )}
            <div className="u-maxWidth-550px u-display-inlineBlock">
              <div className="typo-h6 u-textAlign-left u-margin-bottom-sm">
                Summary Description:
              </div>
              <textarea
                cols={200}
                rows={3}
                className="typo-title u-lineHeight-15 u-width-100 u-padding"
                onChange={this.handleTextChange('shortDescription')}
                value={shortDescription}
              />
              <div className="typo-h6 u-textAlign-left u-margin-bottom-sm u-margin-top-lg">
                Tagline:
              </div>
              <textarea
                cols={200}
                rows={1}
                className="typo-title u-lineHeight-15 u-width-100 u-padding"
                onChange={this.handleTextChange('tagline')}
                value={tagline}
              />
            </div>
            <UiButton variant="dismiss" className="u-margin-top" onClick={this.handleReset}>
              Want to see the suggestions again?
            </UiButton>
          </div>
        )}
        <JobFlowButtons
          disabled={buttonDisabled}
          onPrev={editing && step === 0 ? undefined : handlePrev}
          onNext={handleNext}
          onSkip={editing ? undefined : onSkip}
          editing={editing && step === 3}
          saving={saving}
        />
      </div>
    );
  }
}

export const JobFlowShortDescription = connect(mapStateToProps)(JobFlowShortDescriptionComponent);
